// client/src/pages/Settings.js
import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, InputAdornment, Alert, CircularProgress,
    Paper, Container, IconButton, Grid, Divider, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import { KeyRound, Eye, EyeOff, Check, X } from 'lucide-react';
import API from '../api/client'; // Adjust path based on your directory structure

const Settings = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Independent visibility toggles for enterprise usability
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Real-time password validation helpers
    const passwordCriteria = {
        length: newPassword.length >= 8,
        hasNumber: /\d/.test(newPassword),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>_]/.test(newPassword),
    };

    const isPasswordValid = Object.values(passwordCriteria).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!isPasswordValid) {
            setError('Please ensure your new password meets all security criteria.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('The confirmation password does not match your new password.');
            return;
        }

        try {
            setIsSubmitting(true);

            const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
            const backupId = storedUser.id || storedUser._id || storedUser.employeeId;

            await API.post('/auth/change-password', {
                currentPassword,
                newPassword,
                userId: backupId
            });

            setSuccess('Password updated successfully. Please use your new credentials on your next login.');
            setCurrentPassword('');
            newPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error("❌ Password modification failure:", err);
            setError(err.response?.data?.message || 'Failed to sync your credentials with the identity provider.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            {/* Header Area */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
                    Account Settings
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b', mt: 0.5 }}>
                    Manage your workspace authentication credentials and security parameters.
                </Typography>
            </Box>

            <Divider sx={{ mb: 5, borderColor: '#e2e8f0' }} />

            {/* Split Content Area */}
            <Grid container spacing={4}>

                {/* Left Side: Context & Requirements */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ pr: { md: 2 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                            Authentication Security
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6, mb: 3 }}>
                            We recommend changing your credentials periodically. Avoid reusing passwords from other external applications.
                        </Typography>

                        {/* Password Requirements UI Card */}
                        <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#f8fafc', borderColor: '#e2e8f0', borderRadius: '12px' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#64748b', tracking: '0.05em' }}>
                                New Password Rules
                            </Typography>
                            <List dense sx={{ p: 0, mt: 1 }}>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{ minWidth: 28, color: passwordCriteria.length ? '#10b981' : '#94a3b8' }}>
                                        {passwordCriteria.length ? <Check size={16} strokeWidth={3} /> : <X size={16} />}
                                    </ListItemIcon>
                                    <ListItemText primary="At least 8 characters long" primaryTypographyProps={{ variant: 'body2', color: passwordCriteria.length ? '#0f172a' : '#64748b' }} />
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{ minWidth: 28, color: passwordCriteria.hasNumber ? '#10b981' : '#94a3b8' }}>
                                        {passwordCriteria.hasNumber ? <Check size={16} strokeWidth={3} /> : <X size={16} />}
                                    </ListItemIcon>
                                    <ListItemText primary="Contains at least one number" primaryTypographyProps={{ variant: 'body2', color: passwordCriteria.hasNumber ? '#0f172a' : '#64748b' }} />
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{ minWidth: 28, color: passwordCriteria.hasSpecial ? '#10b981' : '#94a3b8' }}>
                                        {passwordCriteria.hasSpecial ? <Check size={16} strokeWidth={3} /> : <X size={16} />}
                                    </ListItemIcon>
                                    <ListItemText primary="Contains a special character" primaryTypographyProps={{ variant: 'body2', color: passwordCriteria.hasSpecial ? '#0f172a' : '#64748b' }} />
                                </ListItem>
                            </List>
                        </Paper>
                    </Box>
                </Grid>

                {/* Right Side: Form Controls */}
                <Grid item xs={12} md={8}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 4, borderRadius: '12px', borderColor: '#e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.05)' }}
                    >
                        {error && <Alert severity="error" variant="standard" sx={{ mb: 3, borderRadius: '8px', fontWeight: 500 }}>{error}</Alert>}
                        {success && <Alert severity="success" variant="standard" sx={{ mb: 3, borderRadius: '8px', fontWeight: 500 }}>{success}</Alert>}

                        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>

                            <TextField
                                fullWidth
                                required
                                size="small"
                                type={showCurrent ? "text" : "password"}
                                label="Current Password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                disabled={isSubmitting}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ color: '#94a3b8' }}>
                                            <KeyRound size={16} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowCurrent(!showCurrent)} edge="end" sx={{ color: '#94a3b8' }}>
                                                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <Divider sx={{ borderStyle: 'dashed', borderColor: '#f1f5f9' }} />

                            <TextField
                                fullWidth
                                required
                                size="small"
                                type={showNew ? "text" : "password"}
                                label="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isSubmitting}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ color: '#94a3b8' }}>
                                            <KeyRound size={16} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowNew(!showNew)} edge="end" sx={{ color: '#94a3b8' }}>
                                                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <TextField
                                fullWidth
                                required
                                size="small"
                                type={showConfirm ? "text" : "password"}
                                label="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isSubmitting}
                                error={confirmPassword && newPassword !== confirmPassword}
                                helperText={confirmPassword && newPassword !== confirmPassword ? "Passwords do not match" : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ color: '#94a3b8' }}>
                                            <KeyRound size={16} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end" sx={{ color: '#94a3b8' }}>
                                                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {/* Action Button alignment to flex-end (classic standard layout) */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting || !isPasswordValid}
                                    variant="contained"
                                    disableElevation
                                    sx={{
                                        textTransform: 'none',
                                        bgcolor: '#0f172a', // Clean black/slate primary button
                                        fontWeight: 600,
                                        borderRadius: '6px',
                                        px: 3,
                                        py: 1,
                                        '&:hover': { bgcolor: '#1e293b' },
                                        '&:disabled': { bgcolor: '#f1f5f9', color: '#94a3b8' }
                                    }}
                                >
                                    {isSubmitting ? <CircularProgress size={20} sx={{ color: '#94a3b8' }} /> : 'Save changes'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Settings;